import { useEffect, useState } from "react";

/**
 * useIdleStatus — returns "online" while the tab is visible AND the user has
 * interacted within `idleAfterMs`, otherwise "away".
 *
 * Activity events watched: pointermove / pointerdown / keydown / scroll / touchstart.
 * Tab visibility (Page Visibility API) is also factored in so switching away
 * to another tab instantly flips to "away".
 *
 *   const status = useIdleStatus();          // 5-minute default
 *   <PresenceDot status={status} />
 */
export default function useIdleStatus(idleAfterMs = 5 * 60_000) {
  const [status, setStatus] = useState("online");
  useEffect(() => {
    let lastActivity = Date.now();
    let timer = null;

    const recompute = () => {
      const idle = Date.now() - lastActivity > idleAfterMs;
      const hidden = document.visibilityState !== "visible";
      setStatus(idle || hidden ? "away" : "online");
    };

    const bump = () => { lastActivity = Date.now(); recompute(); };
    const events = ["pointermove", "pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    document.addEventListener("visibilitychange", recompute);

    // Re-evaluate every 30s so a long-running idle naturally flips to "away"
    // even if the user never triggers another event.
    timer = setInterval(recompute, 30_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener("visibilitychange", recompute);
      if (timer) clearInterval(timer);
    };
  }, [idleAfterMs]);
  return status;
}
